import React, { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useGetProductsQuery } from "../../api/productApi";
import ProductCard from "../productsArchive/ProductCard";
import type { TransformedProduct } from "../../types/productApiTypes";

interface RelatedProductsProps {
  currentProduct?: TransformedProduct;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({
  currentProduct,
}) => {
  if (
    !currentProduct ||
    !currentProduct.categories ||
    currentProduct.categories.length === 0
  ) {
    return null;
  }

  const firstCategoryId = currentProduct.categories[0].id.toString();

  const {
    data: relatedProductsData,
    isLoading,
    isError,
  } = useGetProductsQuery({
    category: [firstCategoryId],
    per_page: 6, // Fetch 6 to filter out the current one and get up to 5
  });

  const relatedProducts = useMemo(() => {
    if (!relatedProductsData?.products || !currentProduct) return [];
    let uniqueRelatedProducts = relatedProductsData.products
      .filter((p) => p.id !== currentProduct.id)
      .slice(0, 5);

    return uniqueRelatedProducts;
  }, [relatedProductsData, currentProduct]);

  if (isLoading) {
    return (
      <div className="related-products__wrapper">
        <div className="related-products">
          <Skeleton
            height={30}
            width={280}
            style={{ marginBottom: "0.5rem", display: "block" }}
          />
          <Skeleton
            height={24}
            width={220}
            style={{ marginBottom: "1.5rem", display: "block" }}
          />
          <div className="related-products__swiper-container related-products__swiper-container--loading">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="product-card-skeleton"
                style={{ minWidth: "200px", marginRight: "16px" }}
              >
                <Skeleton height={180} />
                <Skeleton
                  height={20}
                  style={{ marginTop: "8px", marginBottom: "4px" }}
                />
                <Skeleton height={16} width="60%" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError || !relatedProducts || relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="related-products__wrapper">
      <div className="related-products">
        <h2 className="related-products__slogan">Yarım Asırlık Tecrübe</h2>
        <h3 className="related-products__title">Benzer Ürünler</h3>
        <div className="related-products__swiper-container">
          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={4} // Default to 4 slides
            navigation
            breakpoints={{
              // For screens < 640px wide, show 2 slides
              320: { slidesPerView: 2, spaceBetween: 10 }, // Smallest mobile
              // For screens >= 640px and < 768px wide, show 3 slides
              640: { slidesPerView: 3, spaceBetween: 16 },
              // For screens >= 768px and < 1024px wide, show 3 slides (or 4 if design allows)
              768: { slidesPerView: 3, spaceBetween: 16 },
              // For screens >= 1024px wide, show 4 slides
              1024: { slidesPerView: 4, spaceBetween: 16 },
            }}
            className="related-products__swiper"
          >
            {relatedProducts.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard product={product} viewMode="grid" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;
